import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    output,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { Item } from "../../pojo/Item";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25ClickOutsideDirective } from "../s25-click-outside/s25.click.outside.directive";
import { ContactRole } from "../../services/organization.service";
import { ContactService } from "../../services/contact.service";
import { FormAssignmentRecipient } from "../data-ingestion/data.ingestion.service";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { FileUploadModule } from "../s25-file-upload/s25.file.upload.module";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25Util } from "../../util/s25-util";
import { DBContact } from "../student-housing/student.housing.service";
import { Bind } from "../../decorators/bind.decorator";
import {
    parseRecipientEmailsFromFile,
    parseRecipientListText,
    RecipientListParseResult,
} from "./email-recipient-list.util";
import { Nullable } from "../../pojo/Util";

type RecipientImportClose = { validEmails: string[] };

export function fromContactRole(contactRole: ContactRole): FormAssignmentRecipient {
    return {
        contId: contactRole.itemId ? Number(contactRole.itemId) : undefined,
        email: contactRole.itemEmail ?? "",
        displayName: contactRole.itemName ?? "",
    };
}

/**
 * One addressee row - TO, CC or BCC - as chips over a contact picker, a role picker and a
 * free-text field. The list of recipients belongs to the caller; everything used to arrive at
 * it (which roles are ticked, what the picker has open) stays in here.
 */
@TypeManagerDecorator("s25-ng-email-recipients")
@Component({
    selector: "s25-ng-email-recipients",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25MultiselectModule,
        S25IconButtonComponent,
        S25DropdownPaginatedModule,
        S25ClickOutsideDirective,
        S25ModalModule,
        FileUploadModule,
        S25ButtonComponent,
    ],
    template: `
        <div class="recipient-header">
            <label
                ><strong>{{ label() }}</strong></label
            >
            @if (orgMembers().length) {
                <div class="recipient-action-bar">
                    <button
                        class="action-btn"
                        [class.active]="allMembersSelected()"
                        (click)="toggleAllMembers()"
                        [disabled]="disabled()"
                    >
                        @if (allMembersSelected()) {
                            <span class="action-check">✓</span>
                        }
                        All {{ memberLabel() }}
                    </button>
                    @if (availableRoles().length) {
                        <div class="role-btn-wrap" s25-ng-click-outside (clickOutside)="showRolePanel.set(false)">
                            <button
                                class="action-btn"
                                [class.active]="selectedRoles().length > 0"
                                (click)="toggleRolePanel()"
                                [disabled]="disabled()"
                            >
                                @if (selectedRoles().length > 0) {
                                    <span class="action-check">✓</span>
                                }
                                Select by Role
                            </button>
                            @if (showRolePanel()) {
                                <div class="role-panel">
                                    <s25-ng-generic-multiselect-dropdown
                                        [items]="roleDropdownItems()"
                                        [value]="selectedRoles()"
                                        [hasSelectAll]="true"
                                        [autoOpen]="true"
                                        (valueChange)="onRoleChange($event); showRolePanel.set(false)"
                                    />
                                </div>
                            }
                        </div>
                    }
                    <s25-ng-multiselect-search-criteria
                        [type]="'contacts'"
                        [buttonText]="'Select Contacts'"
                        [selectedItems]="pickerItems()"
                        [modelBean]="contactModelBean"
                        [disabled]="disabled()"
                        [popoverOnBody]="true"
                        (close)="onContactsChange($event)"
                    />
                    <button
                        class="action-btn"
                        type="button"
                        (click)="openRecipientImport()"
                        [disabled]="disabled() || importing()"
                    >
                        Upload list
                    </button>
                </div>
            } @else {
                <div class="recipient-action-bar">
                    <s25-ng-multiselect-search-criteria
                        [type]="'contacts'"
                        [buttonText]="'Select Contacts'"
                        [selectedItems]="pickerItems()"
                        [modelBean]="contactModelBean"
                        [disabled]="disabled()"
                        [popoverOnBody]="true"
                        (close)="onContactsChange($event)"
                    />
                    <button
                        class="action-btn"
                        type="button"
                        (click)="openRecipientImport()"
                        [disabled]="disabled() || importing()"
                    >
                        Upload list
                    </button>
                </div>
            }
        </div>
        <div class="recipient-box">
            <div class="chip-input-container">
                @for (recipient of recipients(); track recipient.email) {
                    <span
                        class="chip"
                        [class.chip-unresolved]="recipient.needsName && resolveNames()"
                        [title]="recipient.email ?? ''"
                    >
                        <span class="chip-name">
                            @if (recipient.displayName || recipient.firstName || recipient.familyName) {
                                {{ recipient.displayName ?? recipient.firstName + " " + recipient.familyName }}
                                @if (!recipient.contId) {
                                    ({{ recipient.email }})
                                }
                            } @else {
                                {{ recipient.email }}
                            }
                        </span>
                        @if (resolveNames() && recipient.needsName) {
                            <s25-ng-icon-button
                                [type]="'warningYield'"
                                [ariaLabel]="'Name required — click to add'"
                                (clicked)="editRequested.emit(recipient)"
                            />
                        } @else if (
                            resolveNames() && !recipient.contId && (recipient.firstName || recipient.familyName)
                        ) {
                            <s25-ng-icon-button
                                [type]="'pencil'"
                                [ariaLabel]="'Edit name'"
                                (clicked)="editRequested.emit(recipient)"
                            />
                        }
                        <button class="chip-remove" (click)="removeRecipient(recipient.email!)" [disabled]="disabled()">
                            ×
                        </button>
                    </span>
                }
                <input
                    type="text"
                    [ngModel]="draftEmail()"
                    (ngModelChange)="draftEmail.set($event)"
                    (keydown)="onAdditionalEmailKeydown($event)"
                    class="inline-input"
                    placeholder="Type email and press Enter"
                    [disabled]="disabled()"
                />
            </div>
        </div>

        <s25-ng-modal
            #recipientImportModal
            [title]="'Upload recipient list'"
            [type]="'custom'"
            [size]="'md'"
            [onBody]="true"
        >
            <ng-template #s25ModalBody>
                <div class="recipient-import">
                    <p class="recipient-import__hint">
                        Upload a .csv, .txt, or .xlsx file, or paste addresses below (comma, semicolon, or newline
                        separated).
                    </p>
                    <div class="recipient-import-upload">
                        <s25-ng-file-upload
                            [showFiles]="false"
                            [extRegex]="importFileExtRegex"
                            [onAdd]="handleImportFileUpload"
                        />
                    </div>
                    <label class="recipient-import__label" for="recipient-import-paste">Paste recipients</label>
                    <textarea
                        id="recipient-import-paste"
                        class="recipient-import__paste cn-form__control"
                        rows="4"
                        [ngModel]="importPasteText()"
                        (ngModelChange)="onImportPasteChange($event)"
                        placeholder="email1@example.com, email2@example.com"
                    ></textarea>
                    @if (importFileError()) {
                        <p class="recipient-import__error" role="alert">{{ importFileError() }}</p>
                    }
                    @if (importParsing()) {
                        <p class="recipient-import__summary">Reading file…</p>
                    } @else if (importParseResult(); as parseResult) {
                        <p class="recipient-import__summary">
                            {{ importSummaryText(parseResult) }}
                        </p>
                    }
                    <div class="recipient-import-footer">
                        <button type="button" class="aw-button aw-button--outline" (click)="closeRecipientImport()">
                            Cancel
                        </button>
                        <s25-ng-button
                            [type]="'primary'"
                            [disabled]="!canConfirmImport()"
                            [onClick]="confirmRecipientImport"
                        >
                            Add to {{ label() }}
                        </s25-ng-button>
                    </div>
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: block;
            margin-bottom: 1rem;
        }

        .recipient-header {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 4px;
        }

        .recipient-header label {
            margin: 0;
        }

        .recipient-action-bar {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        }

        .action-btn {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 5px 12px;
            background: var(--color--surface-2-bg, #f5f5f5);
            border: 1px solid var(--color--border, #ddd);
            border-radius: 4px;
            font-size: 13px;
            color: var(--color--text, #333);
            cursor: pointer;
            white-space: nowrap;
        }

        .action-btn:hover:not(:disabled) {
            background: rgba(37, 115, 167, 0.08);
            border-color: #2573a7;
            color: #2573a7;
        }

        .action-btn.active {
            background: #e8f4fd;
            border-color: #2573a7;
            color: #2573a7;
        }

        .action-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .action-check {
            font-size: 11px;
        }

        /* Normalize the multiselect-search-criteria trigger to match .action-btn */
        .recipient-action-bar ::ng-deep .multiSelectButton {
            padding: 5px 12px !important;
            background: var(--color--surface-2-bg, #f5f5f5) !important;
            border: 1px solid var(--color--border, #ddd) !important;
            border-radius: 4px !important;
            font-size: 13px !important;
            color: var(--color--text, #333) !important;
            line-height: normal !important;
            height: auto !important;
        }

        .recipient-action-bar ::ng-deep .multiSelectButton:hover {
            background: rgba(37, 115, 167, 0.08) !important;
            border-color: #2573a7 !important;
            color: #2573a7 !important;
        }

        .role-btn-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
        }

        .role-panel {
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            z-index: 100;
            min-width: 180px;
        }

        /* Hide the Select2 trigger inside the role-panel — only the list shows */
        .role-panel ::ng-deep .s25-multiselect-popup-position-header,
        .role-panel ::ng-deep a.select2-choice,
        .role-panel ::ng-deep .select2-drop:not(.select2-drop-active) {
            display: none !important;
        }

        .recipient-box {
            border: 1px solid var(--color--border, #e0e0e0);
            border-radius: 6px;
            padding: 10px 12px;
            background: var(--color--surface-bg, white);
        }

        .recipient-box:focus-within {
            border-color: #2573a7;
            box-shadow: 0 0 0 2px rgba(37, 115, 167, 0.1);
        }

        .chip-input-container {
            flex: 1;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 6px;
            min-height: 28px;
        }

        .inline-input {
            flex: 1;
            min-width: 200px;
            border: none;
            outline: none;
            padding: 4px;
            font-size: 14px;
            background: transparent;
            color: inherit;
        }

        .inline-input::placeholder {
            color: var(--color--text-muted, #999);
        }

        .chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 3px 8px;
            background: var(--color--surface-2-bg, #f5f5f5);
            color: var(--color--text, #333);
            border: 1px solid var(--color--border, #ddd);
            border-radius: 12px;
            font-size: 13px;
            white-space: nowrap;
        }

        .chip-name {
            max-width: 180px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .chip-remove {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 16px;
            cursor: pointer;
            padding: 0;
            width: 16px;
            height: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .chip-remove:hover:not(:disabled) {
            color: var(--color--text, #333);
        }

        .chip-remove:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .chip-unresolved {
            border-color: var(--color--warning, #f9a825);
            background: var(--color--surface-bg-warning, #fff8e1);
        }

        @media (max-width: 600px) {
            .inline-input {
                min-width: 100px;
            }
            .recipient-action-bar {
                flex-wrap: wrap;
            }
            .chip-name {
                max-width: 120px;
            }
        }

        .recipient-import__hint {
            margin: 0 0 0.75rem;
            font-size: 13px;
            color: var(--color--text-muted, #666);
        }

        .recipient-import-upload ::ng-deep .drop-zone {
            display: none;
        }

        .recipient-import__label {
            display: block;
            margin: 0.75rem 0 0.25rem;
            font-weight: 600;
            font-size: 13px;
        }

        .recipient-import__paste {
            width: 100%;
            box-sizing: border-box;
        }

        .recipient-import__summary {
            margin: 0.75rem 0 0;
            font-size: 13px;
            color: var(--color--text-muted, #666);
        }

        .recipient-import__error {
            margin: 0.5rem 0 0;
            font-size: 13px;
            color: var(--color--danger, #c62828);
        }

        .recipient-import-footer {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            width: 100%;
            margin-top: 1rem;
            padding-top: 0.75rem;
            border-top: 1px solid var(--color--border, #e0e0e0);
        }
    `,
})
export class S25EmailRecipientsComponent {
    /** The row's heading, e.g. "TO:". */
    readonly label = input.required<string>();
    readonly recipients = model<FormAssignmentRecipient[]>([]);
    /** An address typed but not yet committed with Enter. The caller still has to send it. */
    readonly draftEmail = model("");
    readonly orgMembers = input<ContactRole[]>([]);
    readonly memberLabel = input("Members");
    readonly disabled = input(false);
    /**
     * Turns on the named-contact flow: a typed address is looked up, and one with no contact
     * behind it is flagged for the caller to name.
     */
    readonly resolveNames = input(false);

    readonly editRequested = output<FormAssignmentRecipient>();
    /** A typed address that matched no contact, so the caller can prompt for a name. */
    readonly nameNeeded = output<string>();
    readonly removed = output<string>();

    protected readonly importFileExtRegex = "(\\.csv|\\.txt|\\.xlsx)$";
    protected readonly importParseResult = signal<RecipientListParseResult | null>(null);
    protected readonly importParsing = signal(false);
    protected readonly importFileError = signal<string | null>(null);
    protected readonly importPasteText = signal("");
    protected readonly importing = signal(false);

    protected readonly canConfirmImport = computed(() => {
        const parseResult = this.importParseResult();
        return !!parseResult?.validEmails.length && !this.importParsing();
    });

    private readonly recipientImportModal = viewChild.required("recipientImportModal", {
        read: S25ModalComponent<RecipientImportClose>,
    });

    protected readonly selectedRoles = signal<DropDownItem[]>([]);
    protected readonly showRolePanel = signal(false);
    /** The contact picker keeps its own selection here; it predates signals. */
    protected readonly contactModelBean: { selectedItems?: S25ItemI[] } = {};

    private readonly uniqueOrgMembers = computed(() => [
        ...new Map(this.orgMembers().map((member) => [member.itemId, member])).values(),
    ]);

    protected readonly availableRoles = computed(() => {
        const seen = new Set<number>();
        return this.orgMembers()
            .map((member) => member.role)
            .filter((role) => role?.itemId && !seen.has(role.itemId) && seen.add(role.itemId));
    });

    protected readonly roleDropdownItems = computed<DropDownItem[]>(() =>
        this.availableRoles().map((role) => ({
            itemId: role?.itemId,
            val: role?.itemId,
            txt: role?.itemName,
            itemName: role?.itemName,
        })),
    );

    /** Only contacts the picker knows about; typed addresses have no id for it to match. */
    protected readonly pickerItems = computed<S25ItemI[]>(() =>
        this.recipients()
            .filter((recipient) => recipient.contId)
            .map((recipient) => this.toS25Item(recipient)),
    );

    constructor() {
        effect(() => {
            this.orgMembers();
            untracked(() => {
                const roles = this.selectedRoles();
                if (roles.length) this.onRoleChange(roles);
            });
        });
    }

    protected toggleRolePanel() {
        this.showRolePanel.update((open) => !open);
    }

    protected allMembersSelected(): boolean {
        const all = this.membersAsRecipients();
        if (!all.length) return false;

        const ids = new Set(
            this.recipients()
                .map((recipient) => recipient.contId)
                .filter(Boolean),
        );
        return all.every((member) => member.contId && ids.has(member.contId));
    }

    protected toggleAllMembers() {
        if (!this.allMembersSelected()) {
            return this.setRecipients(this.mergeRecipients(this.recipients(), this.membersAsRecipients()));
        }

        const memberContIds = this.memberContIds();
        this.setRecipients(
            this.recipients().filter((recipient) => !recipient.contId || !memberContIds.has(recipient.contId)),
        );
        this.selectedRoles.set([]);
    }

    protected onRoleChange(selected: Nullable<DropDownItem[]>) {
        this.selectedRoles.set(selected ?? []);
        const memberContIds = this.memberContIds();
        const nonMembers = this.recipients().filter(
            (recipient) => !recipient.contId || !memberContIds.has(recipient.contId),
        );
        const roleMembers = selected?.length ? this.membersForRoles(selected) : [];
        this.setRecipients(this.mergeRecipients(nonMembers, roleMembers));
    }

    protected onContactsChange(selected: S25ItemI[]) {
        const pickerRecipients = selected.map((item) => this.fromS25Item(item));
        // Preserve typed-email entries (no contId) — the picker doesn't manage them
        const typedChips = this.recipients().filter((recipient) => !recipient.contId);
        this.recipients.set([...pickerRecipients, ...typedChips]);
        this.syncRolesFromRecipients(pickerRecipients);
    }

    protected onAdditionalEmailKeydown(event: KeyboardEvent) {
        if (event.key !== "Enter") return;
        event.preventDefault();
        void this.addAdditionalEmailChip();
    }

    async addAdditionalEmailChip() {
        const email = this.draftEmail().trim();
        if (!email) return;

        if (!this.resolveNames()) {
            this.recipients.update((current) => [...current, { email }]);
            this.draftEmail.set("");
            return;
        }

        this.draftEmail.set("");
        const contacts = await ContactService.getContactsByEmails([email]);
        const contact = contacts?.[0];
        this.recipients.update((current) => [...current, this.recipientForEmail(email, contact)]);
        if (!contact) {
            this.nameNeeded.emit(email);
        }
    }

    protected async openRecipientImport(): Promise<void> {
        this.resetImportState();
        const closeData = (await this.recipientImportModal().open()) as RecipientImportClose | undefined;
        if (closeData?.validEmails?.length) {
            await this.applyImportedEmails(closeData.validEmails);
        }
    }

    protected closeRecipientImport(): void {
        void this.recipientImportModal().close();
    }

    @Bind
    protected confirmRecipientImport(): void {
        const validEmails = this.importParseResult()?.validEmails ?? [];
        void this.recipientImportModal().close({ validEmails });
    }

    protected importSummaryText(parseResult: RecipientListParseResult): string {
        const parts = [`${parseResult.validEmails.length} valid`];
        if (parseResult.invalidCount) {
            parts.push(`${parseResult.invalidCount} invalid skipped`);
        }
        if (parseResult.duplicateCount) {
            parts.push(`${parseResult.duplicateCount} duplicates merged`);
        }
        if (parseResult.cappedCount) {
            parts.push(`${parseResult.cappedCount} over limit skipped`);
        }
        return parts.join(", ");
    }

    protected onImportPasteChange(text: string): void {
        this.importPasteText.set(text);
        this.importFileError.set(null);
        this.importParseResult.set(parseRecipientListText(text));
    }

    @Bind
    protected async handleImportFileUpload(file: S25File): Promise<void> {
        const fileRef = file.fileRef;
        if (!fileRef) return;

        this.importParsing.set(true);
        this.importFileError.set(null);
        try {
            this.importParseResult.set(await parseRecipientEmailsFromFile(fileRef));
            this.importPasteText.set("");
        } catch (caughtError) {
            this.importParseResult.set(null);
            this.importFileError.set(caughtError instanceof Error ? caughtError.message : "Could not read file");
        } finally {
            this.importParsing.set(false);
        }
    }

    protected async applyImportedEmails(emails: string[]): Promise<void> {
        if (!emails.length) return;

        this.importing.set(true);
        try {
            const contacts = await ContactService.getContactsByEmails(emails);
            const contactByEmail = new Map(
                contacts.filter((contact) => contact.email).map((contact) => [contact.email!.toLowerCase(), contact]),
            );
            const incoming: FormAssignmentRecipient[] = [];
            for (const email of emails) {
                const contact = contactByEmail.get(email.toLowerCase());
                incoming.push(this.recipientForEmail(email, contact));
                if (this.resolveNames() && !contact) {
                    this.nameNeeded.emit(email);
                }
            }
            this.setRecipients(this.mergeRecipients(this.recipients(), incoming));
        } catch (caughtError) {
            S25Util.showError(caughtError);
        } finally {
            this.importing.set(false);
        }
    }

    private resetImportState(): void {
        this.importParseResult.set(null);
        this.importParsing.set(false);
        this.importFileError.set(null);
        this.importPasteText.set("");
    }

    private recipientForEmail(email: string, contact: DBContact | undefined): FormAssignmentRecipient {
        if (!contact) {
            if (this.resolveNames()) {
                return { email, needsName: true };
            }
            return { email };
        }
        const firstName = contact.firstName ?? "";
        const familyName = contact.familyName ?? "";
        return {
            contId: contact.contId,
            email,
            firstName,
            familyName,
            displayName: (contact.fullName ?? `${firstName} ${familyName}`).trim(),
        };
    }

    protected removeRecipient(email: string) {
        const updated = this.recipients().filter((recipient) => recipient.email !== email);
        this.setRecipients(updated);

        // Drop any role whose members are all gone from the list
        const recipientIds = new Set(updated.map((recipient) => recipient.contId).filter(Boolean));
        this.selectedRoles.update((roles) =>
            roles.filter((role) => {
                const roleId = Number(role.val ?? role.itemId);
                return this.uniqueOrgMembers()
                    .filter((member) => member.role?.itemId === roleId)
                    .some((member) => recipientIds.has(Number(member.itemId)));
            }),
        );

        this.removed.emit(email);
    }

    private setRecipients(value: FormAssignmentRecipient[]) {
        this.recipients.set(value);
        // Only sync contacts (with contId) back to the picker's internal state
        this.contactModelBean.selectedItems = value
            .filter((recipient) => recipient.contId)
            .map((recipient) => this.toS25Item(recipient));
    }

    private memberContIds(): Set<number | undefined> {
        return new Set(
            this.membersAsRecipients()
                .map((member) => member.contId)
                .filter(Boolean),
        );
    }

    private membersAsRecipients(): FormAssignmentRecipient[] {
        return this.uniqueOrgMembers().map(fromContactRole);
    }

    private membersForRoles(roles: DropDownItem[]): FormAssignmentRecipient[] {
        const roleIds = new Set(roles.map((role) => Number(role.val ?? role.itemId)));
        const seen = new Set<number | string>();
        return this.orgMembers()
            .filter(
                (member) =>
                    S25Util.isDefined(member.role?.itemId) &&
                    roleIds.has(member.role.itemId) &&
                    !seen.has(member.itemId) &&
                    seen.add(member.itemId),
            )
            .map(fromContactRole);
    }

    private syncRolesFromRecipients(pickerRecipients: FormAssignmentRecipient[]) {
        const recipientIds = new Set(pickerRecipients.map((recipient) => recipient.contId).filter(Boolean));
        this.selectedRoles.update((roles) =>
            roles.filter((role) => {
                const roleId = Number(role.val ?? role.itemId);
                return this.uniqueOrgMembers()
                    .filter((member) => member.role?.itemId === roleId)
                    .some((member) => recipientIds.has(Number(member.itemId)));
            }),
        );
    }

    private mergeRecipients(
        existing: FormAssignmentRecipient[],
        incoming: FormAssignmentRecipient[],
    ): FormAssignmentRecipient[] {
        const contIds = new Set(existing.map((recipient) => recipient.contId).filter(Boolean));
        const emails = new Set(existing.map((recipient) => recipient.email?.toLowerCase()).filter(Boolean) as string[]);
        return [
            ...existing,
            ...incoming.filter((recipient) => {
                if (recipient.contId && contIds.has(recipient.contId)) {
                    return false;
                }
                const lower = recipient.email?.toLowerCase();
                return !(lower && emails.has(lower));
            }),
        ];
    }

    private toS25Item(recipient: FormAssignmentRecipient): S25ItemI {
        const name =
            recipient.displayName ??
            (recipient.firstName && recipient.familyName
                ? `${recipient.firstName} ${recipient.familyName}`
                : (recipient.firstName ?? recipient.familyName ?? recipient.email ?? ""));
        return { itemId: recipient.contId, itemName: name, itemDesc: recipient.email, itemTypeId: Item.Ids.Contact };
    }

    private fromS25Item(item: S25ItemI): FormAssignmentRecipient {
        return {
            contId: item.itemId ? Number(item.itemId) : undefined,
            email: item.itemDesc,
            displayName: item.itemName ?? "",
        };
    }
}
